import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
  ApiDashboardElement,
  ApiImageGallery,
} from '@highimpact-frontend/api-types';
import type { Layouts } from 'react-grid-layout';
import { type Layout } from 'react-grid-layout';
import { hydrateDashboardLibraryState } from '../actions';
import type {
  ConfigWidgetPanelStateType,
  DashboardFormState,
  DeleteWidgetConfirmation,
  EditingState,
  ImageGallerySelector,
  ResourceSelector,
  WidgetForm,
} from './initials';
import { initialDashboard } from './initials';
import {
  ImageGalleryWidgetType,
  TextType,
  WidgetType,
} from './widget';

// -- Payload types

interface SetDashboardLayoutPayload {
  type: 'grids' | 'reports';
  dashboardId: number;
  layoutName: string;
  layoutValues: Layout[];
}

type SimplifiedLayoutType = Layouts[string];

// TODO: Move to right place.
const adaptLayout = (
  layout: SimplifiedLayoutType[number],
  width: number,
  height: number,
  _asMinSize: boolean,
  colsNumber: number,
  rowsNumber: number,
  gridWidth: number,
  gridHeight: number
) => {
  const rowSize = Math.round(gridHeight / rowsNumber);
  const widgetWidth = Math.round(gridWidth / colsNumber) * layout.w;
  const widgetHeight = rowSize * layout.h;

  const targetAspectRatio = width / height;
  const idealHeight = widgetWidth / targetAspectRatio;

  if (idealHeight - 5 <= widgetHeight && widgetHeight <= idealHeight + 5) {
    return layout;
  }
  const newHeight = Math.round(idealHeight / rowSize);
  return { ...layout, h: newHeight };
};

// -- Slice definition

const dashboard_slice = createSlice({
  name: 'dashboard-slice',

  initialState: initialDashboard,

  reducers: {
    setTextValue(
      state,
      {
        payload,
      }: PayloadAction<{
        widgetId: string;
        gridId: number;
        gridType: 'grids' | 'reports';
        value: string;
      }>
    ) {
      (
        state[payload.gridType][payload.gridId].widgets[
          payload.widgetId
        ] as TextType
      ).props.value = payload.value;
    },
    setWidget(
      state,
      {
        payload,
      }: PayloadAction<{
        widgetId: string;
        gridId: number;
        gridType: 'grids' | 'reports';
        widget: WidgetType;
      }>
    ) {
      (state[payload.gridType][payload.gridId].widgets[
        payload.widgetId
      ] as WidgetType) = payload.widget;
    },
    setWidgetFormIsOpen(state, action: PayloadAction<WidgetForm>) {
      if (action.payload.state === 'closed') {
        state.widgetFormIsOpen = { state: 'closed' };
      } else {
        state.widgetFormIsOpen = {
          state: 'open',
          elementType: action.payload.elementType,
          formTitle: action.payload.formTitle,
          specificWidgetType: action.payload.specificWidgetType,
          widgetGroupId: action.payload.widgetGroupId,
        };
      }
    },
    setDashboardForm(state, { payload }: PayloadAction<DashboardFormState>) {
      state.dashboardForm = payload;
    },
    setDeleteWidgetConfirmation(
      state,
      action: PayloadAction<DeleteWidgetConfirmation>
    ) {
      state.deleteConfirmationModalIsOpen = action.payload;
    },
    setWidgetConfigFormIsOpen(state, action: PayloadAction<ResourceSelector>) {
      if (action.payload.state === 'closed') {
        state.resourceSelectorConfig = { state: 'closed' };
      } else {
        state.resourceSelectorConfig = {
          dashboardId: action.payload.dashboardId,
          widgetId: action.payload.widgetId,
          projectId: action.payload.projectId,
          siteId: action.payload.siteId,
          state: 'open',
          grid_type: action.payload.grid_type,
        };
      }
    },
    setImageGalleryWidgetConfig(
      state,
      action: PayloadAction<ImageGallerySelector>
    ) {
      if (action.payload.state === 'closed') {
        state.imageGallerySelectorConfig = { state: 'closed' };
      } else {
        state.imageGallerySelectorConfig = {
          dashboardId: action.payload.dashboardId,
          widgetId: action.payload.widgetId,
          projectId: action.payload.projectId,
          siteId: action.payload.siteId,
          state: action.payload.state,
          tags: action.payload.tags,
          grid_type: action.payload.grid_type,
        };
      }
    },
    addImageGalleryConfigTag(state, action: PayloadAction<string>) {
      if (
        state.imageGallerySelectorConfig.state === 'open' &&
        state.imageGallerySelectorConfig.tags.length === 0
      ) {
        const newTags = [
          ...state.imageGallerySelectorConfig.tags,
          action.payload,
        ];
        state.imageGallerySelectorConfig.tags = newTags;
      }
    },
    setDashboardLayout(
      state,
      { payload: p }: PayloadAction<SetDashboardLayoutPayload>
    ) {
      if (state[p.type][p.dashboardId] !== undefined) {
        state[p.type][p.dashboardId]!.layouts[p.layoutName] = p.layoutValues;
      } else {
        console.error(
          `[DashboardSlice][setDashboardLayout]: unknown dashboard id ${p.dashboardId}`
        );
      }
    },

    /**
     * Set editing state attribute
     *
     * @param state Current dashboard store state
     * @param action New editing value
     */
    setEditing(state, action: PayloadAction<EditingState>) {
      const editing = action.payload;
      state.editing = editing;
    },
    /**
     * Set creating widget
     */
    setWidgetConfigPanelState(
      state,
      action: PayloadAction<ConfigWidgetPanelStateType>
    ) {
      const widgetConfigState = action.payload;
      state.ConfigWidgetPanelState = widgetConfigState;
    },
    /**
     * Manual data insertion state management
     */
    setaddingData(state, action: PayloadAction<number | null>) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      state.addingData = { _tag: 'AddingData', editing: action.payload! };
    },
    setnotaddingData(state, _action: PayloadAction<number | null>) {
      state.addingData = { _tag: 'NotAddingData' };
    },
    setAwaitingDataAdditionResponse(state) {
      state.addingData = { _tag: 'AwaitingDataAdditionResponse' };
    },
    setSuccessfulAddition(state) {
      state.addingData = { _tag: 'SuccessfulAddition' };
    },
    setAdditionFailure(state) {
      state.addingData = { _tag: 'AdditionFailure' };
    },
    setWidgetLayout(
      state,
      action: PayloadAction<{
        dashboardId: number;
        widgetId: string;
        width: number;
        heigth: number;
        asMinSize: boolean;
      }>
    ) {
      const colsNumber = state.colsNumber;
      const rowsNumber = state.rowsNumber;
      const gridWidth = state.currentGridWidth;
      const gridHeight = state.currentGridHeight;

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (state.grids[action.payload.dashboardId]!.layouts[
        'lg'
      ]! as unknown as SimplifiedLayoutType) = (
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        state.grids[action.payload.dashboardId]!.layouts[
          'lg'
        ]! as unknown as SimplifiedLayoutType
      ).map((l) => {
        return l.i === action.payload.widgetId
          ? adaptLayout(
              l,
              action.payload.width,
              action.payload.heigth,
              action.payload.asMinSize,
              colsNumber,
              rowsNumber,
              gridWidth,
              gridHeight
            )
          : l;
      });
    },
    setImageId(
      state,
      action: PayloadAction<{
        widgetId: string;
        dashboardId: number;
        resourceId: number;
        grid_type: 'reports' | 'grids';
      }>
    ) {
      state[action.payload.grid_type][action.payload.dashboardId]!.widgets[
        action.payload.widgetId
        // @ts-expect-error Type cannot be resolved due to Redux
      ]!.props!.resource_id = action.payload.resourceId;
    },
    setImageGalleryTags(
      state,
      {
        payload: { gridId, gridType, widgetId, tags },
      }: PayloadAction<{
        gridType: 'grids' | 'reports';
        gridId: number;
        widgetId: string;
        tags: string[];
      }>
    ) {
      if (
        state[gridType][gridId].widgets[widgetId].type !==
        'image-gallery-widget'
      ) {
        throw new Error('Not an Image Gallery Widget. Cannot proceed');
      }
      (
        state[gridType][gridId].widgets[widgetId] as ImageGalleryWidgetType
      ).props.tags = tags;
      (
        (state[gridType][gridId].widgets[widgetId] as ImageGalleryWidgetType)
          .elementData as ApiImageGallery
      ).properties.tags = tags;
    },
    setDashboardElement(
      state,
      {
        payload: { gridId, gridType, widgetId, dashboardElement },
      }: PayloadAction<{
        gridType: 'grids' | 'reports';
        gridId: number;
        widgetId: string;
        dashboardElement: ApiDashboardElement;
      }>
    ) {
      state[gridType][gridId].widgets[widgetId].elementData = dashboardElement;
    },
    updateWidgetName(
      state,
      {
        payload: { gridId, gridType, widgetId, name },
      }: PayloadAction<{
        gridType: 'grids' | 'reports';
        gridId: number;
        widgetId: string;
        name: string;
      }>
    ) {
      const grid = state[gridType][gridId];
      if (!grid) {
        console.info(
          `[updateWidgetName] Unknown grid id: ${gridId} for type ${gridType}`
        );
        return;
      }
      if (!grid.widgets[widgetId]) {
        console.info(
          `[updateWidgetName] Unknown widget id: ${widgetId} in grid ${gridId}`
        );
        return;
      }

      grid.widgets[widgetId].elementData.name = name;
    },
    setCurrentGridSize(
      state,
      action: PayloadAction<{ width: number; height: number }>
    ) {
      state.currentGridHeight = action.payload.height;
      state.currentGridWidth = action.payload.width;
    },
    updateDashboardName(
      state,
      action: PayloadAction<{
        dashboardId: number;
        name: string;
        gridType: 'grids' | 'reports';
      }>
    ) {
      const { dashboardId, gridType, name } = action.payload;
      const dashboard = state[gridType][dashboardId];

      if (!dashboard) {
        console.info(
          `[updateDashboardName] Unknown dashboard id ${dashboardId} (type ${gridType})`
        );
        return;
      }

      dashboard.name = name;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(hydrateDashboardLibraryState, (_, action) => {
      return action.payload.dashboard;
    });
  },
});

export const {
  setTextValue,
  setWidget,
  setWidgetFormIsOpen,
  setImageGalleryWidgetConfig,
  addImageGalleryConfigTag,
  setDashboardForm,
  setWidgetConfigFormIsOpen,
  setDeleteWidgetConfirmation,
  setDashboardLayout,
  setEditing,
  setWidgetConfigPanelState,
  setaddingData,
  setnotaddingData,
  setAwaitingDataAdditionResponse,
  setSuccessfulAddition,
  setAdditionFailure,
  setWidgetLayout,
  setCurrentGridSize,
  setImageId,
  updateDashboardName,
  updateWidgetName,
  setImageGalleryTags,
  setDashboardElement,
} = dashboard_slice.actions;

export default dashboard_slice.reducer;
